import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { Redirect } from 'react-router';

import { Flex } from '@opendoor/bricks/core';

import { ExclusivesLayout } from 'src/components/exclusives/hocs/layoutWrapper';
import ExclusivesQuestionPage from 'src/components/exclusives/pageTypes/ExclusivesQuestionPage';
import TwoColumnFlowPage from 'src/components/flowPage/TwoColumnFlowPage';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import AccountPage from 'src/components/sellerFlow/AccountPage';
import { useMetricsContext } from 'src/helpers/MetricsContext';
import { sellerFlowPagePath } from 'src/routes/pathBuilders';
import { JsonFlowPage as FlowPageType } from 'src/store/flow';
import { useAppSelector } from 'src/store/hooks';
import { isInExclusiveOffersTreatment } from 'src/store/sellerFlow/selectors';

import { getCurrentSlug } from '../../store/router/selectors';

interface FlowPageProps {
  page: FlowPageType;
}

const ExclusivesFlowLayout: React.FC<PropsWithChildren> = ({ children }) => (
  <ExclusivesLayout headerType="NONE" footerType="NONE">
    {children}
  </ExclusivesLayout>
);

const FlowPage: React.FC<FlowPageProps> = () => {
  const pageRef = useRef<string | null>(null);
  // TODO - see if we can use uiCurrentSlug instead of currentSlug
  const currentSlug = useAppSelector(getCurrentSlug);
  const uiCurrentSlug = useAppSelector((state) => state.ui.page?.slug);
  const sellerInputUuid = useAppSelector((state) => state.sellerInput.leadToken);
  const { trackPageView } = useMetricsContext();

  const inExclusiveOffersTreatment = useAppSelector(isInExclusiveOffersTreatment);

  useEffect(() => {
    if (currentSlug) {
      trackPageView({ slug: currentSlug }, { addTiming: true });
    }
  }, [currentSlug, trackPageView]);

  // Only redirect when we've never had a page (because it no longer exists
  // from an old url or the user ended up on a funny slug)
  // Otherwise we're probably waiting for a p/complete page to load
  let page: React.ReactNode =
    pageRef.current === null ? (
      <Redirect to={sellerFlowPagePath({ token: sellerInputUuid, slug: '' })} />
    ) : null;

  if (currentSlug) {
    pageRef.current = currentSlug;
    if (inExclusiveOffersTreatment) {
      page = (
        <ExclusivesFlowLayout>
          {uiCurrentSlug === 'account' ? <AccountPage /> : <ExclusivesQuestionPage />}
        </ExclusivesFlowLayout>
      );
    } else {
      page = <TwoColumnFlowPage />;
    }
  }

  return (
    <ReceptionLoader>
      <Flex flexDirection="column" width="100%">
        {page}
      </Flex>
    </ReceptionLoader>
  );
};

export default FlowPage;
